import { TransferRefundType } from "@edaz/utils/bankAccount/types";
import type { EntityValidationRules } from "../services/validationService";
import CustomerRefundCandidateRefundPaymentResponse from "./customerRefundCandidateRefundPaymentResponse";
import type RefundCounterpartyBankRequest from "./refundCounterpartyBankRequest";
import RefundPaymentType from "./refundPaymentType";
import type RefundTransferPayment from "./refundTransferPaymentResponse";
import type RefundForRefundOfRefundPayment from "./refundForRefundOfRefundPaymentResponse";
import type TransferRefundCounterpartyBankRequest from "./transferRefundCounterpartyBankRequest";
import TransferType from "./transferType";
import TransferPaymentType from "./transferPaymentType";
import type FuelType from "./fuelType";

export interface IChargeRefund {
  officialRecordId: string;
  counterpartyBank: RefundCounterpartyBankRequest;
  transferType?: TransferType | TransferPaymentType;
  fuelType?: FuelType;
  counterpartyName?: string;
  counterpartyStreetAddress?: string;
  counterpartyCityAddress?: string;
  counterpartyCountryCode?: string;
  additionalAvMessage?: string;
}

export interface IRefundCandidate {
  officialRecordId: string;
  counterpartyBank: TransferRefundCounterpartyBankRequest;
  transferType?: TransferPaymentType | TransferRefundType;
  fuelTypeId?: string;
  emailAddress?: string;
  counterpartyName?: string;
  counterpartyStreetAddress?: string;
  counterpartyCityAddress?: string;
  counterpartyCountryId?: string;
  additionalAvMessage?: string;
}

export const RefundPaymentInformationValidationRulesFactory = (
  item:
    | IChargeRefund
    | IRefundCandidate
    | RefundTransferPayment
    | CustomerRefundCandidateRefundPaymentResponse
    | RefundForRefundOfRefundPayment
): EntityValidationRules<
  | IChargeRefund
  | IRefundCandidate
  | RefundTransferPayment
  | CustomerRefundCandidateRefundPaymentResponse
  | RefundForRefundOfRefundPayment
> => {
  const baseValidationRules = {
    counterpartyName: {
      isLength: {
        max: 70,
        optional: true,
      },
    },
    counterpartyStreetAddress: {
      isLength: {
        max: 70,
        optional: true,
      },
    },
    counterpartyCityAddress: {
      isLength: {
        max: 70,
        optional: true,
      },
    },
    additionalAvMessage: {
      isLength: {
        max: 30,
        optional: true,
        customMessage: "max30Length",
      },
    },
  };

  let validationRules:
    | EntityValidationRules<IChargeRefund | IRefundCandidate | RefundTransferPayment>
    | EntityValidationRules<CustomerRefundCandidateRefundPaymentResponse>;
  if (item instanceof CustomerRefundCandidateRefundPaymentResponse) {
    validationRules = {
      ...baseValidationRules,
      counterpartyName: {
        ...baseValidationRules.counterpartyName,
        requiredIf: {
          condition: (entity: CustomerRefundCandidateRefundPaymentResponse) =>
            !!entity.paymentType &&
            [RefundPaymentType.ForeignTransfer, RefundPaymentType.SepaTransfer].includes(entity.paymentType),
        },
      },
      paymentType: {
        required: true,
      },
    };
  } else {
    validationRules = {
      ...baseValidationRules,
      counterpartyName: {
        ...baseValidationRules.counterpartyName,
        requiredIf: {
          condition: (entity: IChargeRefund | IRefundCandidate | RefundTransferPayment) =>
            !!entity.transferType &&
            [
              TransferType.Foreign,
              TransferType.Sepa,
              TransferPaymentType.Foreign,
              TransferPaymentType.Sepa,
              TransferRefundType.FOREIGN,
              TransferRefundType.SEPA,
            ].includes(entity.transferType),
        },
      },
      transferType: {
        required: true,
      },
    };
  }

  return validationRules;
};
